import { useState, useEffect } from "react";
import {
    useTheme,
    useMediaQuery,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    Typography,
} from "@mui/material";
import { Helmet } from "react-helmet";

import AiCreditSection from "@/account/AiCreditSection";
import CompanySection from "@/account/CompanySection";
import ContactSection from "@/account/ContactSection";
import CreditsSection from "@/account/CreditsSection";
import GeneralSection from "@/account/GeneralSection";
import ManagementSection from "@/account/ManagementSection";
import OrganizationsSection from "@/account/OrganizationsSection";
import PaymentSection from "@/account/PaymentSection";
import PersonalSection from "@/account/PersonalSection";
import SecuritySection from "@/account/SecuritySection";
import SubscriptionSection from "@/account/SubscriptionSection";
import { request } from "@/Api";
import { PERMISSION_EDIT_LEGAL_ENTITIES } from "@/auth/constants";
import useEntitlements from "@/auth/useEntitlements";
import { PageTitle } from "@/common/CommonComponents";
import { FEATURE_MULTI_PERSON, FEATURE_AI_INTAKE } from "@/constants";

export default function Account() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [loading, setLoading] = useState(true);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState("");
    const [account, setAccount] = useState({});
    const [organizations, setOrganizations] = useState([]);
    const [company, setCompany] = useState({});

    const { hasFeature, hasPermission } = useEntitlements();

    useEffect(() => {
        setLoading(true);

        const requests = [];

        requests.push(
            request.get("legal-entities/default").then((response) => {
                setCompany({ ...response.data });
            }),
        );

        requests.push(
            request.get("organizations").then((response) => {
                setOrganizations([...response.data]);
            }),
        );

        requests.push(
            request.get("account").then((response) => {
                setAccount({ ...response.data });
            }),
        );

        Promise.all(requests)
            .catch((error) => {
                setError("Failed to load account settings.");
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const handleCloseSave = () => {
        setSaved(false);
    };
    const handleCloseError = () => {
        setError("");
    };

    const containerProps = isMobile
        ? {
              flexDirection: "column",
              spacing: 2,
          }
        : {
              flexDirection: "row",
              spacing: 6,
              sx: { marginTop: "36px" },
          };

    return (
        <Grid container wrap="nowrap" {...containerProps}>
            <Helmet>
                <PageTitle title="Manage Account" />
            </Helmet>

            <Grid item>
                <Typography variant="h2">My Account</Typography>
                <p>Manage your account settings here.</p>
            </Grid>
            {loading ? (
                <Grid item textAlign="center" width="100%">
                    <CircularProgress sx={{ marginTop: "48px" }} />
                </Grid>
            ) : (
                <Grid item container direction="column" spacing={3}>
                    <PersonalSection
                        data={account}
                        setData={setAccount}
                        setSaved={setSaved}
                        setError={setError}
                    />
                    <ContactSection
                        data={account}
                        setData={setAccount}
                        setSaved={setSaved}
                        setError={setError}
                        disableEdit={account.has_google_account}
                        warning={
                            account.has_google_account
                                ? "This account has an associated Google account for login, changing email is not available."
                                : null
                        }
                    />
                    <SecuritySection
                        data={account}
                        setSaved={setSaved}
                        setError={setError}
                        disableEdit={account.has_google_account}
                        warning={
                            account.has_google_account
                                ? "This account has an associated Google account for login, changing password is not available"
                                : null
                        }
                    />
                    {/* <CreditsSection
                        data={account.payment_subscription}
                        setData={(payment_subscription) => setAccount({...account, payment_subscription})}
                        setSaved={setSaved}
                        setError={setError}
                    /> */}
                    {account.payment_subscription && (
                        <>
                            <SubscriptionSection
                                data={account.payment_subscription}
                                setData={(payment_subscription) =>
                                    setAccount({
                                        ...account,
                                        payment_subscription,
                                    })
                                }
                                setSaved={setSaved}
                                setError={setError}
                            />
                            <PaymentSection
                                data={account.payment_subscription}
                                setData={(payment_subscription) =>
                                    setAccount({
                                        ...account,
                                        payment_subscription,
                                    })
                                }
                                setSaved={setSaved}
                            />
                        </>
                    )}
                    {/* <GeneralSection setSaved={setSaved} setError={setError} /> */}
                    {hasPermission(PERMISSION_EDIT_LEGAL_ENTITIES) && (
                        <CompanySection
                            data={company}
                            setData={setCompany}
                            setSaved={setSaved}
                            setError={setError}
                        />
                    )}
                    {hasFeature(FEATURE_MULTI_PERSON) && (
                        <OrganizationsSection
                            data={organizations}
                            setData={setOrganizations}
                            setSaved={setSaved}
                            setError={setError}
                        />
                    )}
                    {hasFeature(FEATURE_AI_INTAKE) && (
                        <AiCreditSection data={account} />
                    )}
                    <ManagementSection data={account} setError={setError} />
                </Grid>
            )}

            <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                open={saved}
                onClose={handleCloseSave}
                autoHideDuration={2000}
            >
                <Alert
                    variant="filled"
                    severity="success"
                    sx={{ width: "100%" }}
                    onClose={handleCloseSave}
                >
                    Account updated
                </Alert>
            </Snackbar>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={error != ""}
                onClose={handleCloseError}
            >
                <Alert
                    variant="filled"
                    severity="error"
                    sx={{ width: "100%" }}
                    onClose={handleCloseError}
                >
                    {error}
                </Alert>
            </Snackbar>
        </Grid>
    );
}
